import { Box, Button, Link as MaterialLink, Typography, makeStyles } from '@material-ui/core';
import { FunctionComponent, useState } from 'react';
import { Link } from 'react-router-dom';
import tick from '../assets/svgs/tick.svg';
import { REFERRAL_URL_TYPE } from '../constants';
import { generateReferralURL } from '../helpers/ReferralUtils';
import { Analytics } from '../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../helpers/analytics/AnalyticsEvent';
import { useAuthStore } from '../store/AuthStore';
import { ConnectInitializeUser } from '../store/StoreTypes';
import { AlertModal } from './AlertModal';
import { AlertStatus } from './ModalTypes';

const useStyles = makeStyles(() => ({
  root: {},
  copyButton: {
    color: 'white',
    borderRadius: 50,
    height: 50,
    width: 200,
  },
}));

export const CopyReferralLinkButton: FunctionComponent = () => {
  const classes = useStyles();
  const { referralHash } = useAuthStore((state) => state.user as ConnectInitializeUser);
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const [alertState, setAlertState] = useState(AlertStatus.Success);
  const domainUrl = process.env.REACT_APP_BASE_URL;

  async function copyToClipboard(link: string) {
    try {
      await navigator.clipboard.writeText(link);
      Analytics.track({
        event: AnalyticsEventName.h_referral_url_copied,
        url: window.location.href,
      });
      setAlertState(AlertStatus.Success);
      setOpenCopyModal(true);
    } catch (error) {
      Analytics.track({
        event: AnalyticsEventName.h_referral_url_failed,
        type: REFERRAL_URL_TYPE.LINK,
        error: String(error),
      });
      setAlertState(AlertStatus.Fail);
      setOpenCopyModal(true);
    }
  }

  function copy() {
    const generatedLink = generateReferralURL(referralHash);
    copyToClipboard(generatedLink);
  }

  function handleClose() {
    setOpenCopyModal(false);
  }

  const copyToClipboardFailedMessage =
    'There was a problem copying your link to the clipboard. Please try again or copy the link below.';

  return (
    <div data-testid="copy-referral-link-button">
      <Button
        variant="contained"
        color="primary"
        classes={{ root: classes.copyButton }}
        onClick={copy}
      >
        <Box fontSize={16} fontWeight={700}>
          Nerva Referral Link
        </Box>
      </Button>
      <AlertModal
        open={openCopyModal}
        statusIcon={
          alertState === AlertStatus.Success && (
            <img src={tick} width={85} height={85} alt="copy succeeded" />
          )
        }
        title={
          <Typography variant="h5">
            {alertState === AlertStatus.Success
              ? 'Referral Link copied to your clipboard'
              : copyToClipboardFailedMessage}
          </Typography>
        }
        body={
          <>
            <Box
              bgcolor="white"
              width="470px"
              textAlign="center"
              borderRadius="30px"
              pt="10px"
              pb="10px"
              mt="30px"
            >
              <Typography>{`${domainUrl}/patient/${referralHash}`}</Typography>
            </Box>
            <Box maxWidth={400} textAlign="center" marginBottom="20px" mt="30px">
              <Typography>
                Paste this link into an email, text or other message to refer Nerva to your patient.
              </Typography>
            </Box>
          </>
        }
        action={
          <Box
            width={160}
            textAlign="center"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Box mb="15px">
              <Link to="/linked-patients" onClick={handleClose}>
                <MaterialLink component="button" color="primary">
                  <Typography>More info</Typography>
                </MaterialLink>
              </Link>
            </Box>
            <Button
              onClick={handleClose}
              variant="contained"
              color="primary"
              size="small"
              fullWidth
            >
              Great!
            </Button>
          </Box>
        }
      />
    </div>
  );
};
